export interface PremadeTemplate {
  name: string;
  description: string;
  content: string;
}

export const PREMADE_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Standard Security Guard Services",
    description: "General on-site security guard contract covering patrol, access control, and incident reporting for commercial properties.",
    content: `SECURITY GUARD SERVICES CONTRACT

This Security Guard Services Contract ("Contract") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SCOPE OF SERVICES

The Provider agrees to furnish uniformed security guard services at the Client's premises, including but not limited to:

• On-site patrol and surveillance of the property
• Access control and visitor management
• Incident response and reporting
• Emergency coordination with local law enforcement
• Daily activity logs and shift reports

{{scope_of_work}}

Project Description:
{{project_description}}

──────────────────────────────────────────────────

2. STAFFING & SCHEDULE

The Provider shall deploy qualified security personnel as follows:

Estimated Hours: {{estimated_hours}} per week
Personnel shall be uniformed, licensed, and background-checked in accordance with applicable state regulations.

──────────────────────────────────────────────────

3. TERM AND DURATION

{{contract_length}}

This contract shall commence on the date of execution and remain in effect for the duration specified above, unless terminated earlier in accordance with the terms herein. Either party may request renewal or extension prior to expiration.

──────────────────────────────────────────────────

4. COMPENSATION

Estimated Total Cost: \${{estimated_cost}} per billing period
Payment Terms: {{payment_terms}}

Late payments shall incur a fee of 1.5% per month on the outstanding balance. The Provider reserves the right to suspend services if payment is not received within fifteen (15) days past the due date.

──────────────────────────────────────────────────

4. PROVIDER RESPONSIBILITIES

• Supply qualified, licensed, and insured security officers
• Maintain required licenses and insurance for the contract duration
• Provide supervision and quality assurance
• Deliver written incident and shift reports
• Replace personnel who do not meet performance standards within 48 hours

──────────────────────────────────────────────────

5. CLIENT RESPONSIBILITIES

• Provide accurate information regarding premises and known hazards
• Grant reasonable access to facilities and necessary resources
• Notify Provider promptly of any changes in conditions or requirements
• Comply with all payment obligations

──────────────────────────────────────────────────

6. TERM & TERMINATION

This Contract shall commence on the Effective Date and continue for the agreed service period. Either Party may terminate with thirty (30) days' written notice. In the event of material breach, the non-breaching Party may terminate immediately with written notice.

Upon termination, the Client shall pay for all services rendered through the termination date.

──────────────────────────────────────────────────

7. LIABILITY & INSURANCE

The Provider shall maintain commercial general liability insurance with minimum coverage of $1,000,000 per occurrence. The Provider shall not be liable for indirect, incidental, or consequential damages.

──────────────────────────────────────────────────

8. CONFIDENTIALITY

Both Parties agree to maintain confidentiality of all proprietary and sensitive information exchanged during this Contract. This obligation survives termination for two (2) years.

──────────────────────────────────────────────────

9. ADDITIONAL TERMS

{{terms_and_conditions}}

Additional Notes:
{{proposal_notes}}

──────────────────────────────────────────────────

10. SIGNATURES

By signing below, the Parties acknowledge that they have read, understood, and agree to be bound by this Contract.

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Armed Security Services Agreement",
    description: "Contract for armed security personnel — includes firearm compliance, use-of-force policies, and enhanced liability provisions.",
    content: `ARMED SECURITY SERVICES AGREEMENT

This Armed Security Services Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}

──────────────────────────────────────────────────

1. PURPOSE

The Provider shall furnish armed security officers to protect the Client's property, assets, and personnel. All officers shall be licensed to carry firearms in accordance with applicable state and federal law.

──────────────────────────────────────────────────

2. SCOPE OF ARMED SERVICES

• Armed patrol and deterrent presence
• Threat assessment and rapid response
• VIP/executive protection (if applicable)
• Coordination with local law enforcement agencies
• Incident documentation and evidence preservation

{{scope_of_work}}

Project Description:
{{project_description}}

──────────────────────────────────────────────────

3. FIREARM & USE-OF-FORCE COMPLIANCE

A. All armed officers shall possess valid state-issued armed security licenses.
B. Officers shall carry only Provider-approved and registered firearms.
C. The Provider's Use-of-Force Policy shall govern all armed interactions — force is authorized only as a last resort in defense of life.
D. All firearm discharges shall be reported within 1 hour and fully documented within 24 hours.
E. Officers shall undergo annual firearms qualification and bi-annual use-of-force training.

──────────────────────────────────────────────────

4. COMPENSATION

Estimated Hours: {{estimated_hours}}
Estimated Total Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Armed security services carry a premium rate that reflects enhanced training, licensing, and insurance requirements.

──────────────────────────────────────────────────

5. INSURANCE & LIABILITY

A. The Provider shall maintain:
   • Commercial General Liability: $2,000,000 per occurrence
   • Professional Liability (Errors & Omissions): $1,000,000
   • Workers' Compensation as required by law
B. The Provider assumes liability for the actions of its armed personnel while on duty.
C. The Client shall not direct armed officers to use force — all tactical decisions rest with the Provider's chain of command.

──────────────────────────────────────────────────

6. TERM & TERMINATION

This Agreement shall commence on the Effective Date. Either Party may terminate with thirty (30) days' written notice. Immediate termination is available upon material breach.

──────────────────────────────────────────────────

7. CONFIDENTIALITY

All security protocols, threat assessments, and operational details shared between the Parties are strictly confidential and shall not be disclosed without written consent.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Event Security Contract",
    description: "Short-term contract for concerts, festivals, corporate events, and gatherings — covers crowd management, access control, and emergency plans.",
    content: `EVENT SECURITY CONTRACT

This Event Security Contract ("Contract") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}

──────────────────────────────────────────────────

1. EVENT DETAILS

{{project_description}}

──────────────────────────────────────────────────

2. SECURITY SERVICES PROVIDED

The Provider agrees to furnish the following event security services:

• Perimeter security and access control at all entry/exit points
• Crowd management and flow control
• VIP area security and escort services
• Bag checks and prohibited items screening
• Parking lot patrol and traffic direction
• Emergency response coordination
• Post-event site sweep and lockdown

{{scope_of_work}}

──────────────────────────────────────────────────

3. STAFFING

The Provider shall deploy the following personnel:

Estimated Hours: {{estimated_hours}} (total across all officers)

All personnel shall arrive at least 1 hour before the event for briefing and positioning. Officers shall remain on-site until the venue is cleared and secured.

──────────────────────────────────────────────────

4. EMERGENCY ACTION PLAN

The Provider shall prepare and submit an Emergency Action Plan (EAP) at least 72 hours prior to the event. The EAP shall include:

• Evacuation routes and assembly points
• Medical emergency response protocols
• Communication channels between security and event staff
• Coordination procedures with local police, fire, and EMS
• Severe weather contingency plans

──────────────────────────────────────────────────

5. COMPENSATION

Estimated Total Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

A 50% deposit is due upon signing this Contract. The remaining balance is due within 7 days after the event concludes.

──────────────────────────────────────────────────

6. CANCELLATION POLICY

• Cancellation more than 14 days before event: Full refund of deposit
• Cancellation 7–14 days before event: 50% of deposit retained
• Cancellation less than 7 days before event: Full deposit retained

──────────────────────────────────────────────────

7. LIABILITY

The Provider shall maintain event-specific liability insurance. The Client shall notify the Provider of all known risks, expected attendance, and any special circumstances related to the event.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Residential Community Security Agreement",
    description: "Ongoing contract for gated communities, HOAs, and apartment complexes — covers concierge security, patrol routes, and resident access protocols.",
    content: `RESIDENTIAL COMMUNITY SECURITY AGREEMENT

This Residential Community Security Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}

──────────────────────────────────────────────────

1. PURPOSE

The Provider shall furnish security services for the Client's residential community, ensuring the safety and well-being of residents, guests, and property.

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

• Gate/lobby concierge and access control
• Resident and visitor verification
• Vehicle patrol of community streets and parking areas
• Foot patrol of common areas, pools, and recreational facilities
• Package and delivery management
• Noise complaint and policy violation response
• Coordination with local law enforcement as needed
• Monthly security summary reports to HOA/management

{{scope_of_work}}

Community Description:
{{project_description}}

──────────────────────────────────────────────────

3. SCHEDULE & STAFFING

Estimated Hours: {{estimated_hours}} per week

Security personnel shall be professional, uniformed, and trained in customer service and conflict de-escalation appropriate for a residential environment.

──────────────────────────────────────────────────

4. RESIDENT ACCESS PROTOCOLS

A. The Provider shall maintain a current authorized resident/vehicle database supplied by the Client.
B. Guests shall be verified via phone or intercom with the resident before entry.
C. Service providers and deliveries shall follow a check-in/check-out process.
D. Emergency vehicles shall be granted immediate, unimpeded access.

──────────────────────────────────────────────────

5. COMPENSATION

Estimated Monthly Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

──────────────────────────────────────────────────

6. TERM & RENEWAL

This Agreement shall commence on the Effective Date for an initial term of twelve (12) months and shall automatically renew for successive 12-month periods unless either Party provides sixty (60) days' written notice of non-renewal.

──────────────────────────────────────────────────

7. LIABILITY & INSURANCE

The Provider shall maintain commercial general liability insurance with minimum coverage of $1,000,000 per occurrence and provide the Client with a certificate of insurance upon request.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Construction Site Security Contract",
    description: "Contract for securing active construction sites — covers after-hours patrols, equipment theft prevention, and OSHA safety compliance.",
    content: `CONSTRUCTION SITE SECURITY CONTRACT

This Construction Site Security Contract ("Contract") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}

──────────────────────────────────────────────────

1. PURPOSE

The Provider shall furnish security services for the Client's active construction site to protect equipment, materials, and property from theft, vandalism, and unauthorized access.

──────────────────────────────────────────────────

2. SCOPE OF SERVICES

• After-hours perimeter patrol and monitoring
• Gate access control during and after work hours
• Equipment and materials inventory verification
• Unauthorized entry detection and response
• Incident documentation with photographs
• Coordination with local police for trespassing/theft
• Fire watch services (if required)
• Daily security logs submitted to site superintendent

{{scope_of_work}}

Site Description:
{{project_description}}

──────────────────────────────────────────────────

3. SCHEDULE & STAFFING

Estimated Hours: {{estimated_hours}} per week

Security coverage is typically required during non-working hours (evenings, weekends, and holidays). Schedule may be adjusted as construction phases progress.

──────────────────────────────────────────────────

4. SAFETY COMPLIANCE

A. All security personnel shall comply with OSHA construction site safety regulations.
B. Officers shall wear appropriate PPE (hard hat, high-visibility vest, steel-toed boots) when on active construction areas.
C. The Provider shall participate in site safety briefings as required.
D. Security shall not operate heavy equipment or interfere with construction activities.

──────────────────────────────────────────────────

5. THEFT & LOSS PREVENTION

A. The Provider shall maintain a log of all equipment and materials present during each shift.
B. Any discrepancies, signs of tampering, or breaches shall be reported immediately to the Client and documented.
C. The Provider shall photograph and timestamp perimeter conditions at the start and end of each shift.

──────────────────────────────────────────────────

6. COMPENSATION

Estimated Total Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Billing is based on hours worked. Overtime and holiday rates may apply per the agreed rate schedule.

──────────────────────────────────────────────────

7. TERM & TERMINATION

This Contract shall remain in effect for the duration of the construction project. Either Party may terminate with fourteen (14) days' written notice.

──────────────────────────────────────────────────

8. LIABILITY & INSURANCE

The Provider shall maintain commercial general liability and workers' compensation insurance. The Provider shall name the Client as an additional insured upon request.

──────────────────────────────────────────────────

9. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

10. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

CLIENT:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];
